import { resolveThemeToken } from '@/composables/useThemeTokens'

/**
 * Mermaid `base` theme variables mapped to ui-lib semantic tokens. Mermaid derives the
 * rest (secondary/tertiary fills, cluster and actor borders, …) from these.
 */
export const MERMAID_THEME_TOKENS = {
  background: '--bg-primary',
  primaryColor: '--bg-status-accent',
  primaryTextColor: '--text-primary',
  primaryBorderColor: '--icon-tertiary',
  secondaryColor: '--bg-secondary',
  secondaryTextColor: '--text-primary',
  secondaryBorderColor: '--border-heavy',
  tertiaryColor: '--bg-tertiary',
  tertiaryTextColor: '--text-primary',
  tertiaryBorderColor: '--border-heavy',
  lineColor: '--icon-secondary',
  textColor: '--text-primary',
  titleColor: '--text-primary',
  mainBkg: '--bg-status-accent',
  nodeBorder: '--icon-tertiary',
  nodeTextColor: '--text-primary',
  clusterBkg: '--bg-secondary',
  clusterBorder: '--border-heavy',
  defaultLinkColor: '--icon-secondary',
  edgeLabelBackground: '--bg-primary',
  noteBkgColor: '--bg-status-warning',
  noteTextColor: '--text-primary',
  noteBorderColor: '--border-heavy',
  actorBkg: '--bg-status-accent',
  actorBorder: '--icon-tertiary',
  actorTextColor: '--text-primary',
  actorLineColor: '--icon-secondary',
  signalColor: '--icon-secondary',
  signalTextColor: '--text-primary',
  labelBoxBkgColor: '--bg-secondary',
  labelBoxBorderColor: '--icon-tertiary',
  labelTextColor: '--text-primary',
  loopTextColor: '--text-primary',
  activationBkgColor: '--bg-secondary',
  activationBorderColor: '--icon-tertiary',
  sequenceNumberColor: '--bg-primary',
  classText: '--text-primary',
} as const

export type MermaidThemeVariables = Record<
  keyof typeof MERMAID_THEME_TOKENS,
  string
>

export const buildMermaidThemeVariables = (
  resolve: (token: string) => string = resolveThemeToken,
): MermaidThemeVariables =>
  Object.fromEntries(
    Object.entries(MERMAID_THEME_TOKENS).map(([variable, token]) => [
      variable,
      resolve(token),
    ]),
  ) as MermaidThemeVariables

/**
 * Variables for `scheme` regardless of the page's scheme: tokens are read from a probe
 * element that pins `color-scheme`, so exports can stay light while the app is dark.
 */
export const buildMermaidThemeVariablesForScheme = (
  scheme: 'light' | 'dark',
): MermaidThemeVariables => {
  const context = document.createElement('div')
  context.style.display = 'none'
  context.style.colorScheme = scheme
  document.documentElement.appendChild(context)
  try {
    return buildMermaidThemeVariables((token) =>
      resolveThemeToken(token, context),
    )
  } finally {
    context.remove()
  }
}
